import { LocationElement, Permission } from "../models";

/**
 * Two-Factor Authentication delivery methods.
 * Must match backend enum: App\Enums\TwoFactor\TwoFactorMethod
 */
export enum TwoFactorMethod {
  SMS = 'sms',
  EMAIL = 'email',
}

export interface Auth {
  access_token: AccessToken | string;
  refresh_token?: string;
  user: AuthUser;
}

export interface AccessToken {
  original: OriginalAccessToken;
}

export interface OriginalAccessToken {
  access_token: string;
}

// Updated login response to handle 2FA and location selection
export interface LoginResponse {
  access_token?: AccessToken;
  refresh_token?: string;
  user?: AuthUser;
  two_factor_required?: boolean;
  method_selection_required?: boolean;
  available_methods?: TwoFactorMethod[];
  location_selection_required?: boolean;
  locations?: LocationForSelection[];
  expires_in_minutes?: number;
  phone_hint?: string;
  email_hint?: string;
  message?: string;
  development_mode?: boolean;
  sms_service_active?: boolean;
  attempts_remaining?: number;
}

// NEW: Send code request interface
export interface TwoFactorSendCodeRequest {
  email: string;
  method: TwoFactorMethod;
}

// NEW: Send code response interface
export interface TwoFactorSendCodeResponse {
  message: string;
  method: TwoFactorMethod;
  expires_in_minutes: number;
  phone_hint?: string;
  email_hint?: string;
  development_mode?: boolean;
  sms_service_active?: boolean;
  attempts_remaining?: number;
}

// Two Factor Authentication interfaces
export interface TwoFactorVerificationRequest {
  code: string;
  email: string;
}

export interface TwoFactorResponse {
  access_token?: string;
  refresh_token?: string;
  user: AuthUser;
  location_selection_required?: boolean;
  locations?: LocationForSelection[];
  message?: string;
  token_type?: string;
}

export interface TwoFactorResendResponse {
  message: string;
  expires_at?: string;
  expires_in_minutes?: number;
  development_mode?: boolean;
  sms_service_active?: boolean;
  attempts_remaining?: number;
}

export interface TwoFactorBackupCodeRequest {
  backup_code: string;
  email: string;
}

// Two Factor status response from backend
export interface TwoFactorStatusResponse {
  data: {
    enabled: boolean;
    phone: string | null;
    has_valid_phone: boolean;
    backup_codes_count: number;
    backup_codes_generated_at: string | null;
    pending_verification: boolean;
    global_enabled: boolean;
    development_mode: boolean;
    sms_service_active: boolean;
  };
}

// Response from refresh endpoint
export interface RefreshResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  user?: RefreshUser;
}

export interface RefreshUser {
  id: number;
  name: string;
  surname?: string;
  email: string;
  permissions: Permission[];
  locations: LocationElement[];
  roles?: string[];
  global_roles?: string[];
  location_id?: number;
  avatar?: string;
  status?: string;
  global_status?: string;
  phone?: string;
  is_admin_view?: boolean;
}

export interface AuthUser {
  id: number;
  name: string;
  surname?: string;
  email: string;
  permissions?: Permission[];
  locations?: LocationElement[];
  roles?: string[];
  global_roles?: string[];
  location_id?: number;
  avatar?: string;
  status?: string;
  global_status?: string;
  phone?: string;
  electronic_signature?: string;
  is_admin_view?: boolean;
}

// Two Factor state management
export interface TwoFactorState {
  required: boolean;
  expiresAt: string | null;
  attemptsRemaining: number;
  isVerifying: boolean;
  error: string | null;
  developmentMode?: boolean;
  smsServiceActive?: boolean;
  userRole?: 'parent' | 'employee' | null;
  availableMethods?: TwoFactorMethod[];
}

// Password Reset interfaces
export interface PasswordResetRequestResponse {
  message: string;
  methods: TwoFactorMethod[];
  phone_hint?: string;
  email_hint?: string;
  user_type: 'user' | 'parent';
}

export interface PasswordResetVerifyResponse {
  message: string;
  reset_token: string;
}

export interface PasswordResetChangeResponse {
  message: string;
}

// Location Selection interfaces
export interface LocationForSelection {
  id: number;
  title: string;
  avatar?: string | null;
  city?: string | null;
  state?: string | null;
  is_admin_view?: boolean;
  status?: string;
}

export interface LocationSelectionRequest {
  location_id: number;
}

export interface LocationSelectionResponse {
  message: string;
  access_token: string;
  refresh_token: string;
  token_type: string;
  user: AuthUser;
}

export interface ChangeLocationRequest {
  location_id: number;
}

export interface ChangeLocationResponse {
  message: string;
  location: LocationForSelection;
  user?: AuthUser;
  roles?: string[];
  permissions?: Permission[];
}

export interface UserLocationsResponse {
  locations: LocationForSelection[];
}

// Discriminated union for the internal login() return value
export type LoginResult =
  | LoginResultSuccess
  | LoginResultTwoFactorMethodSelection
  | LoginResultTwoFactor
  | LoginResultLocationSelection
  | LoginResultFailure;

export interface LoginResultSuccess {
  success: true;
  requiresTwoFactor: false;
}

export interface LoginResultTwoFactorMethodSelection {
  success: false;
  requiresTwoFactor: true;
  methodSelectionRequired: true;
  availableMethods: TwoFactorMethod[];
  phoneHint?: string;
  emailHint?: string;
}

export interface LoginResultTwoFactor {
  success: false;
  requiresTwoFactor: true;
  methodSelectionRequired: false;
}

export interface LoginResultLocationSelection {
  success: true;
  requiresTwoFactor: false;
  requiresLocationSelection: true;
}

export interface LoginResultFailure {
  success: false;
  requiresTwoFactor: false;
}

// Refresh token response interface
export type SyncSessionSource = 'login' | 'refresh';

export interface SyncSessionPayload {
  accessToken: string;
  refreshToken: string;
  user: AuthUser;
  activityAt: number;
  source: SyncSessionSource;
}

export type AuthSyncMessage =
  | { type: 'SESSION_UPDATED'; payload: SyncSessionPayload }
  | { type: 'ACTIVITY'; payload: { at: number } }
  | { type: 'REFRESH_FAILED'; payload: { owner: string } }
  | { type: 'LOGOUT' };

export interface RefreshLockState {
  owner: string;
  expiresAt: number;
}
